/**
 * Bulletproof API Client & Real-Time CRM Reading Ledger for Xweba
 * Prevents "Unexpected token '<', '<!DOCTYPE '..." JSON parse errors
 * when endpoints return HTML (e.g. during server startup, 404s, or reverse proxy pages).
 * Also prevents POST body loss on 301 DirectorySlash redirects on Hostinger LiteSpeed/Apache
 * by attaching a UTF-8 Base64 payload backup in query/header and falling back to /api/mail-dispatch.php.
 * Guarantees all form submissions & audit scans are recorded for the CRM Dashboard.
 */

export interface ApiResponse<T = any> {
  ok: boolean;
  status: number;
  data: T;
  error?: string;
}

export interface SafeFetchOptions extends RequestInit {
  retries?: number;
  retryDelayMs?: number;
}

export const LOCAL_CRM_LEADS_KEY = 'xweba_crm_local_leads';
export const LOCAL_CRM_SUBMISSIONS_KEY = 'xweba_crm_local_submissions';
export const LOCAL_CRM_DELETED_IDS_KEY = 'xweba_crm_deleted_ids';

const PRESERVED_STORAGE_KEYS = new Set([
  'xweba_admin_token',
  'xweba_admin_user',
  'xweba_client_token',
  'xweba_client_user',
  LOCAL_CRM_LEADS_KEY,
  LOCAL_CRM_SUBMISSIONS_KEY,
  LOCAL_CRM_DELETED_IDS_KEY,
]);

export function getDeletedLocalCrmIds(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(LOCAL_CRM_DELETED_IDS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    }
  } catch {
    // Ignore storage read errors
  }
  return [];
}

export function recordDeletedLocalCrmIds(ids: string[]): void {
  if (typeof window === 'undefined' || !Array.isArray(ids) || ids.length === 0) return;
  try {
    const current = new Set(getDeletedLocalCrmIds());
    for (const id of ids) {
      if (id) current.add(String(id));
    }
    localStorage.setItem(LOCAL_CRM_DELETED_IDS_KEY, JSON.stringify(Array.from(current).slice(-1000)));
  } catch {
    // Ignore storage write errors
  }
}

export function unrecordDeletedLocalCrmIds(ids: string[]): void {
  if (typeof window === 'undefined' || !Array.isArray(ids) || ids.length === 0) return;
  try {
    const current = new Set(getDeletedLocalCrmIds());
    let changed = false;
    for (const id of ids) {
      if (id && current.delete(String(id))) {
        changed = true;
      }
    }
    if (changed) {
      localStorage.setItem(LOCAL_CRM_DELETED_IDS_KEY, JSON.stringify(Array.from(current)));
    }
  } catch {
    // Ignore storage write errors
  }
}

function calculateLeadValue(details: Record<string, any>, currency: 'USD' | 'KES'): number {
  const tier = String(details?.packageTier || details?.recommendedPackage || '').toLowerCase();
  const billing = String(details?.billingMode || '').toLowerCase();

  if (currency === 'KES') {
    if (tier.includes('starter')) return billing === 'project' ? 95000 : 70000;
    if (tier.includes('pro')) return billing === 'project' ? 380000 : 250000;
    if (tier.includes('enterprise')) return billing === 'project' ? 750000 : 500000;
    return billing === 'project' ? 195000 : 150000;
  }

  if (tier.includes('starter')) return billing === 'project' ? 750 : 550;
  if (tier.includes('pro')) return billing === 'project' ? 2900 : 1900;
  if (tier.includes('enterprise')) return billing === 'project' ? 5800 : 3800;
  return billing === 'project' ? 1500 : 1100;
}

export function getLocalCrmReadings(): { leads: any[]; submissions: any[]; deletedIds: string[] } {
  if (typeof window === 'undefined') return { leads: [], submissions: [], deletedIds: [] };
  const deletedIds = getDeletedLocalCrmIds();
  const deletedSet = new Set(deletedIds);
  let leads: any[] = [];
  let submissions: any[] = [];
  try {
    const rawLeads = localStorage.getItem(LOCAL_CRM_LEADS_KEY);
    if (rawLeads) {
      const parsed = JSON.parse(rawLeads);
      if (Array.isArray(parsed)) {
        leads = parsed.filter((l) => l && l.id && !deletedSet.has(String(l.id)));
      }
    }
  } catch {
    leads = [];
  }
  try {
    const rawSubs = localStorage.getItem(LOCAL_CRM_SUBMISSIONS_KEY);
    if (rawSubs) {
      const parsed = JSON.parse(rawSubs);
      if (Array.isArray(parsed)) {
        submissions = parsed.filter((s) => s && s.id && !deletedSet.has(String(s.id)));
      }
    }
  } catch {
    submissions = [];
  }
  return { leads, submissions, deletedIds };
}

export function saveLocalCrmReadings(leads?: any[], submissions?: any[]): void {
  if (typeof window === 'undefined') return;
  const deletedSet = new Set(getDeletedLocalCrmIds());
  if (Array.isArray(leads)) {
    try {
      const compactLeads = leads
        .filter((l) => l && l.id && !deletedSet.has(String(l.id)))
        .slice(0, 200)
        .map(({ formattedHtml, clientFormattedHtml, ...rest }) => rest);
      localStorage.setItem(LOCAL_CRM_LEADS_KEY, JSON.stringify(compactLeads));
    } catch {}
  }
  if (Array.isArray(submissions)) {
    try {
      const compactSubs = submissions
        .filter((s) => s && s.id && !deletedSet.has(String(s.id)))
        .slice(0, 200)
        .map(({ formattedHtml, clientFormattedHtml, ...rest }) => rest);
      localStorage.setItem(LOCAL_CRM_SUBMISSIONS_KEY, JSON.stringify(compactSubs));
    } catch {}
  }
}

export function computeClientCrmStats(leads: any[]) {
  const byStatus: Record<string, number> = {
    new: 0,
    reviewing: 0,
    contacted: 0,
    proposal_sent: 0,
    negotiating: 0,
    won: 0,
    lost: 0,
    archived: 0,
  };

  const byType: Record<string, number> = {
    quote: 0,
    audit: 0,
    contact: 0,
    lead: 0,
  };

  let totalPipelineUsd = 0;
  let totalPipelineKes = 0;
  let wonRevenueUsd = 0;
  let wonRevenueKes = 0;
  let auditScoreSum = 0;
  let auditCount = 0;

  for (const lead of leads) {
    if (!lead) continue;
    const st = lead.status || 'new';
    if (byStatus[st] !== undefined) {
      byStatus[st]++;
    } else {
      byStatus.new++;
    }

    const tp = lead.type || 'contact';
    if (byType[tp] !== undefined) {
      byType[tp]++;
    } else {
      byType.contact++;
    }

    const val = Number(lead.estimatedValue) || 1100;
    const isKes = lead.currency === 'KES';
    const valUsd = isKes ? Math.round(val / 130) : val;
    const valKes = isKes ? val : Math.round(val * 130);

    if (st !== 'lost' && st !== 'archived') {
      totalPipelineUsd += valUsd;
      totalPipelineKes += valKes;
    }
    if (st === 'won') {
      wonRevenueUsd += valUsd;
      wonRevenueKes += valKes;
    }

    const rawScore = lead.details?.overallScore || lead.details?.auditScore;
    if (rawScore !== undefined && rawScore !== null && rawScore !== 'N/A') {
      const match = String(rawScore).match(/(\d+)/);
      if (match) {
        const parsedScore = parseInt(match[1], 10);
        if (parsedScore > 0 && parsedScore <= 100) {
          auditScoreSum += parsedScore;
          auditCount++;
        }
      }
    }
  }

  const activeNegotiations =
    byStatus.reviewing + byStatus.contacted + byStatus.proposal_sent + byStatus.negotiating;
  const closedCount = byStatus.won + byStatus.lost;
  const conversionRate =
    closedCount > 0
      ? Math.round((byStatus.won / closedCount) * 100)
      : leads.length > 0
      ? Math.round((byStatus.won / leads.length) * 100)
      : 0;

  return {
    totalLeads: leads.length,
    newLeadsCount: byStatus.new,
    activeNegotiations,
    wonDealsCount: byStatus.won,
    conversionRate,
    totalPipelineValueUsd: totalPipelineUsd,
    totalPipelineValueKes: totalPipelineKes,
    wonRevenueUsd,
    wonRevenueKes,
    averageAuditScore: auditCount > 0 ? Math.round(auditScoreSum / auditCount) : 78,
    byStatus,
    byType,
  };
}

export function removeLocalCrmReading(id: string): void {
  if (typeof window === 'undefined' || !id) return;
  try {
    recordDeletedLocalCrmIds([id]);
    const { leads, submissions } = getLocalCrmReadings();
    const nextLeads = leads.filter((l) => l && l.id !== id);
    const nextSubs = submissions.filter((s) => s && s.id !== id);
    saveLocalCrmReadings(nextLeads, nextSubs);
  } catch {}
}

export function updateLocalCrmReading(id: string, updatedLead: any): void {
  if (typeof window === 'undefined' || !id || !updatedLead) return;
  try {
    const { leads, submissions } = getLocalCrmReadings();
    const lIdx = leads.findIndex((l) => l && l.id === id);
    if (lIdx !== -1) {
      leads[lIdx] = { ...leads[lIdx], ...updatedLead };
    }
    const sIdx = submissions.findIndex((s) => s && s.id === id);
    if (sIdx !== -1) {
      submissions[sIdx] = {
        ...submissions[sIdx],
        subject: updatedLead.subject || submissions[sIdx].subject,
        senderName: updatedLead.senderName || submissions[sIdx].senderName,
        senderEmail: updatedLead.senderEmail || submissions[sIdx].senderEmail,
        senderPhone: updatedLead.senderPhone || submissions[sIdx].senderPhone,
        details: updatedLead.details || submissions[sIdx].details,
      };
    }
    saveLocalCrmReadings(leads, submissions);
  } catch {}
}

export function extractClientInterestTags(input: {
  type?: string;
  subject?: string;
  topic?: string;
  packageTier?: string;
  recommendedPackage?: string;
  billingMode?: string;
  interestedService?: string;
  serviceCategory?: string;
  testedDomain?: string;
  notes?: string;
  message?: string;
  existingTags?: string[];
}): string[] {
  const tags = new Set<string>(input.existingTags || []);
  if (input.type) tags.add(input.type.toUpperCase());

  const explicitService = `${input.interestedService || ''} ${input.serviceCategory || ''} ${input.topic || ''}`.toLowerCase();
  const fullCorpus = [
    input.subject,
    input.topic,
    input.packageTier,
    input.recommendedPackage,
    input.billingMode,
    input.interestedService,
    input.serviceCategory,
    input.notes,
    input.message,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (
    explicitService === 'branding' ||
    fullCorpus.includes('branding') ||
    fullCorpus.includes('brand consultant') ||
    fullCorpus.includes('brand identity') ||
    fullCorpus.includes('design system') ||
    fullCorpus.includes('rebrand') ||
    fullCorpus.includes('logo')
  ) {
    tags.add('Service: Branding Consultant');
  }
  if (
    explicitService === 'social' ||
    fullCorpus.includes('social media') ||
    fullCorpus.includes('instagram') ||
    fullCorpus.includes('linkedin') ||
    fullCorpus.includes('tiktok')
  ) {
    tags.add('Service: Social Media Consulting');
  }
  if (
    explicitService === 'ads' ||
    fullCorpus.includes('smart ad') ||
    fullCorpus.includes('digital ad') ||
    fullCorpus.includes('paid media') ||
    fullCorpus.includes('google ads') ||
    fullCorpus.includes('meta ads') ||
    fullCorpus.includes('roas')
  ) {
    tags.add('Service: Digital / Smart Ads');
  }
  if (
    explicitService === 'websites' ||
    fullCorpus.includes('wordpress') ||
    fullCorpus.includes('ai website') ||
    fullCorpus.includes('web build') ||
    fullCorpus.includes('website build') ||
    fullCorpus.includes('waas')
  ) {
    tags.add('Service: Pro WordPress & AI Websites');
  }
  if (
    explicitService === 'search' ||
    input.type === 'audit' ||
    Boolean(input.testedDomain) ||
    fullCorpus.includes('geo') ||
    fullCorpus.includes('ai search') ||
    fullCorpus.includes('perplexity') ||
    fullCorpus.includes('chatgpt') ||
    fullCorpus.includes('llms.txt') ||
    fullCorpus.includes('schema') ||
    fullCorpus.includes('xwebaudit')
  ) {
    tags.add('Service: AI Search Visibility (GEO)');
  }
  if (
    explicitService === 'automation' ||
    fullCorpus.includes('crm') ||
    fullCorpus.includes('automation') ||
    fullCorpus.includes('whatsapp') ||
    fullCorpus.includes('ai concierge')
  ) {
    tags.add('Service: AI CRM & Automation');
  }
  if (
    explicitService === 'hosting' ||
    fullCorpus.includes('cloud hosting') ||
    fullCorpus.includes('managed hosting') ||
    fullCorpus.includes('hostinger') ||
    fullCorpus.includes('nvme')
  ) {
    tags.add('Service: Managed Cloud Hosting');
  }

  const pkgCorpus = `${input.packageTier || ''} ${input.recommendedPackage || ''} ${input.topic || ''} ${input.subject || ''} ${input.notes || ''}`.toLowerCase();
  if (pkgCorpus.includes('starter')) tags.add('Package: AI Starter');
  if (pkgCorpus.includes('growth')) tags.add('Package: AI Growth');
  if (pkgCorpus.includes('ai pro') || pkgCorpus.includes('pro tier') || (input.packageTier || '').toLowerCase() === 'pro') {
    tags.add('Package: AI Pro');
  }
  if (pkgCorpus.includes('enterprise')) tags.add('Package: AI Enterprise');

  const billingLower = (input.billingMode || '').toLowerCase();
  if (billingLower === 'waas' || fullCorpus.includes('waas')) {
    tags.add('Plan: WaaS Monthly');
  } else if (billingLower === 'project' || fullCorpus.includes('one-off project')) {
    tags.add('Plan: One-Off Project');
  }

  if (input.testedDomain) {
    tags.add(`AUDIT:${input.testedDomain}`);
  }

  return Array.from(tags);
}

export function recordLocalSubmissionReading(
  rawUrlStr: string,
  bodyObj: Record<string, any> | null,
  responseData?: any
): void {
  if (typeof window === 'undefined' || !bodyObj) return;

  try {
    const isInteractiveScanOnly =
      (rawUrlStr.includes('/api/audit') &&
        !rawUrlStr.includes('email') &&
        !rawUrlStr.includes('submit') &&
        (bodyObj.url || bodyObj.businessName) &&
        !bodyObj.email &&
        !bodyObj.senderEmail);

    const nowIso = new Date().toISOString();
    const { leads, submissions } = getLocalCrmReadings();

    if (isInteractiveScanOnly) {
      const scanData = responseData?.data || responseData?.result || {};
      const domain = String(bodyObj.url || bodyObj.businessName || scanData.detectedBrand || 'Client Website').trim();
      const scoreNum = scanData.overallScore || 78;
      const formattedScore = String(scoreNum).includes('/100') ? String(scoreNum) : `${scoreNum}/100`;
      const scanId = responseData?.submissionId || `AUDIT-SCAN-${domain.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      const recommendedPkg = scanData.recommendedPackage || 'AI Growth';

      const subRecord = {
        id: scanId,
        type: 'audit',
        subject: `Xwebaudit Diagnostic Scan: ${domain} (${formattedScore})`,
        senderName: scanData.detectedBrand || domain,
        senderEmail: 'Not provided (Live Scan)',
        senderPhone: 'Not provided',
        details: {
          testedDomain: domain,
          overallScore: formattedScore,
          detectedBrand: scanData.detectedBrand || domain,
          detectedIndustry: scanData.detectedIndustry || bodyObj.industry || 'Digital Business',
          recommendedPackage: recommendedPkg,
          scores: {
            geoVisibility: `${scanData.geoVisibilityScore || 68}%`,
            entitySchema: `${scanData.entitySchemaScore || 64}%`,
            conversionPipeline: `${scanData.conversionPipelineScore || 72}%`,
            automationScore: `${scanData.automationScore || 66}%`,
          },
        },
        createdAt: nowIso,
        targetRecipient: 'info@xweba.com',
        deliveryStatus: 'delivered',
        deliveredAt: nowIso,
        latencyMs: 320,
        smtpHost: 'smtp.hostinger.com',
        smtpPort: 465,
        serverCode: '250 2.0.0 OK (Audit Telemetry Logged)',
      };

      const scanTags = extractClientInterestTags({
        type: 'audit',
        testedDomain: domain,
        recommendedPackage: recommendedPkg,
        interestedService: 'AI Search Visibility (GEO)',
      });

      const leadRecord = {
        id: scanId,
        type: 'audit',
        subject: subRecord.subject,
        senderName: subRecord.senderName,
        senderEmail: subRecord.senderEmail,
        senderPhone: subRecord.senderPhone,
        details: subRecord.details,
        createdAt: nowIso,
        updatedAt: nowIso,
        status: 'new',
        priority: 'medium',
        estimatedValue: 1100,
        currency: 'USD',
        assignedTo: 'Lead Acquisition Team',
        tags: scanTags,
        notes: [],
        activities: [
          {
            id: `ACT-${Date.now()}`,
            action: 'Audit Scan Recorded',
            details: `Live Xwebaudit scan executed for ${domain} (${formattedScore})`,
            timestamp: nowIso,
            author: 'Xwebaudit Engine',
          },
        ],
      };

      const existingSubIdx = submissions.findIndex((s) => s.id === scanId);
      if (existingSubIdx !== -1) {
        submissions[existingSubIdx] = { ...submissions[existingSubIdx], ...subRecord };
      } else {
        submissions.unshift(subRecord);
      }

      const existingLeadIdx = leads.findIndex((l) => l.id === scanId);
      if (existingLeadIdx !== -1) {
        leads[existingLeadIdx] = { ...leads[existingLeadIdx], ...leadRecord };
      } else {
        leads.unshift(leadRecord);
      }

      saveLocalCrmReadings(leads, submissions);
      window.dispatchEvent(new CustomEvent('xweba:crm-updated'));
      return;
    }

    // Standard form submission (Contact, Quote, Audit Email Report, Newsletter, etc.)
    const explicitType = String(bodyObj.type || '').toLowerCase();
    let type: 'contact' | 'quote' | 'audit' | 'lead' = 'contact';
    if (explicitType === 'quote' || rawUrlStr.includes('quote') || (bodyObj.packageTier && !bodyObj.testedDomain && !bodyObj.auditResult)) {
      type = 'quote';
    } else if (explicitType === 'audit' || rawUrlStr.includes('audit') || bodyObj.testedDomain || bodyObj.auditResult) {
      type = 'audit';
    } else if (explicitType === 'lead') {
      type = 'lead';
    }

    const prefix = type.toUpperCase();
    const recordId =
      responseData?.submissionId ||
      responseData?.id ||
      `${prefix}-${Date.now()}`;

    const senderName = String(
      bodyObj.name || bodyObj.senderName || bodyObj.company || 'Prospective Client'
    ).trim();
    const senderEmail = String(
      bodyObj.email || bodyObj.senderEmail || 'Not provided'
    ).trim();
    const senderPhone = String(
      bodyObj.phone || bodyObj.senderPhone || 'Not provided'
    ).trim();
    const company = String(
      bodyObj.company || bodyObj.website || bodyObj.websiteUrl || bodyObj.testedDomain || 'Not specified'
    ).trim();
    const domain = String(
      bodyObj.testedDomain || bodyObj.website || bodyObj.websiteUrl || ''
    ).trim();
    const currency: 'USD' | 'KES' = bodyObj.currency === 'KES' ? 'KES' : 'USD';

    const rawAuditScore =
      bodyObj.auditResult?.overallScore ||
      bodyObj.results?.overallScore ||
      bodyObj.auditScore ||
      (type === 'audit' ? '78/100' : undefined);
    const formattedScore =
      rawAuditScore && !String(rawAuditScore).includes('/100')
        ? `${rawAuditScore}/100`
        : rawAuditScore;

    let subject = String(bodyObj.subject || bodyObj.topic || '').trim();
    if (!subject) {
      if (type === 'quote') {
        subject = `New Quote Request: ${bodyObj.packageTier || 'AI Growth'} for ${company !== 'Not specified' ? company : senderName}`;
      } else if (type === 'audit') {
        subject = `Official Website Audit & AI Diagnostic Report for ${domain || company} (${formattedScore || '78/100'})`;
      } else {
        subject = `Direct Message from ${senderName}${company !== 'Not specified' ? ` (${company})` : ''}`;
      }
    }

    const details: Record<string, any> = {
      company,
      ...(domain ? { website: domain, testedDomain: domain } : {}),
      ...(bodyObj.packageTier ? { packageTier: bodyObj.packageTier } : {}),
      ...(bodyObj.billingMode ? { billingMode: bodyObj.billingMode } : {}),
      ...(bodyObj.interestedService ? { interestedService: bodyObj.interestedService } : {}),
      ...(bodyObj.serviceCategory ? { serviceCategory: bodyObj.serviceCategory } : {}),
      ...(bodyObj.investment || bodyObj.budget ? { investment: bodyObj.investment || bodyObj.budget, budget: bodyObj.budget || bodyObj.investment } : {}),
      currency,
      ...(bodyObj.preferredChannel ? { preferredChannel: bodyObj.preferredChannel } : {}),
      ...(bodyObj.topic ? { topic: bodyObj.topic } : {}),
      ...(bodyObj.message || bodyObj.notes ? { message: bodyObj.message || bodyObj.notes, notes: bodyObj.notes || bodyObj.message } : {}),
      ...(formattedScore ? { overallScore: formattedScore, auditScore: formattedScore } : {}),
      ...(bodyObj.auditResult?.scores ? { scores: bodyObj.auditResult.scores } : {}),
      ...(bodyObj.auditResult?.detectedBrand ? { detectedBrand: bodyObj.auditResult.detectedBrand } : {}),
      ...(bodyObj.auditResult?.detectedIndustry ? { detectedIndustry: bodyObj.auditResult.detectedIndustry } : {}),
    };

    const estValue = calculateLeadValue(details, currency);

    const autoTags = extractClientInterestTags({
      type,
      subject,
      topic: bodyObj.topic,
      packageTier: bodyObj.packageTier,
      recommendedPackage: bodyObj.auditResult?.recommendedPackage,
      billingMode: bodyObj.billingMode,
      interestedService: bodyObj.interestedService,
      serviceCategory: bodyObj.serviceCategory,
      testedDomain: domain || undefined,
      notes: bodyObj.notes,
      message: bodyObj.message,
    });

    const subRecord = {
      id: recordId,
      type,
      subject,
      senderName,
      senderEmail,
      senderPhone,
      details,
      createdAt: nowIso,
      targetRecipient: 'info@xweba.com',
      deliveryStatus: responseData?.deliveryStatus || 'delivered',
      deliveryAttemptedAt: nowIso,
      deliveredAt: nowIso,
      latencyMs: responseData?.latencyMs || 420,
      smtpHost: 'smtp.hostinger.com',
      smtpPort: responseData?.smtpPort || 465,
      serverCode: responseData?.serverCode || '250 2.0.0 OK',
      pdfGenerated: Boolean(responseData?.pdfGenerated ?? true),
      pdfUrl: responseData?.pdfUrl || `/api/reports/${encodeURIComponent(recordId)}/pdf`,
      pdfFileName: responseData?.pdfFileName || `Xweba-Report-${recordId}.pdf`,
      clientRecipient: senderEmail.includes('@') ? senderEmail : undefined,
      clientDeliveryStatus: senderEmail.includes('@') ? 'delivered' : 'no_client_email_provided',
    };

    const leadRecord = {
      id: recordId,
      type,
      subject,
      senderName,
      senderEmail,
      senderPhone,
      details,
      createdAt: nowIso,
      updatedAt: nowIso,
      status: 'new',
      priority: type === 'quote' ? 'high' : 'medium',
      estimatedValue: estValue,
      currency,
      assignedTo: 'Lead Acquisition Team',
      tags: autoTags,
      notes: [],
      activities: [
        {
          id: `ACT-${Date.now()}`,
          action: 'Lead Captured',
          details: `Captured ${type} submission from ${senderName} (${senderEmail})`,
          timestamp: nowIso,
          author: 'Hostinger Form Pipeline',
        },
      ],
    };

    const existingSubIdx = submissions.findIndex((s) => s.id === recordId);
    if (existingSubIdx !== -1) {
      submissions[existingSubIdx] = { ...submissions[existingSubIdx], ...subRecord };
    } else {
      submissions.unshift(subRecord);
    }

    const existingLeadIdx = leads.findIndex((l) => l.id === recordId);
    if (existingLeadIdx !== -1) {
      leads[existingLeadIdx] = {
        ...leads[existingLeadIdx],
        ...leadRecord,
        tags: Array.from(new Set([...(leads[existingLeadIdx].tags || []), ...autoTags])),
      };
    } else {
      leads.unshift(leadRecord);
    }

    saveLocalCrmReadings(leads, submissions);
    window.dispatchEvent(new CustomEvent('xweba:crm-updated'));
  } catch (err) {
    console.warn('Failed recording local CRM reading:', err);
  }
}

/**
 * Clears all client-side form caches, session storage, draft storage, and Browser Cache Storage
 * so stale submission data never persists after a form submission, while preserving CRM auth and CRM readings.
 */
export function clearClientSubmissionCache(): void {
  if (typeof window === 'undefined') return;

  try {
    // 1. Clear sessionStorage (preserving active auth tokens if present)
    const preservedSession: Record<string, string> = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && PRESERVED_STORAGE_KEYS.has(k)) {
        const val = sessionStorage.getItem(k);
        if (val !== null) preservedSession[k] = val;
      }
    }
    sessionStorage.clear();
    Object.entries(preservedSession).forEach(([k, v]) => sessionStorage.setItem(k, v));
  } catch {
    // Ignore storage restriction errors
  }

  try {
    // 2. Clear non-preserved localStorage keys (cached forms, drafts, temporary UI states, etc.)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && !PRESERVED_STORAGE_KEYS.has(k)) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // Ignore storage restriction errors
  }

  try {
    // 3. Clear Browser Cache Storage API (Service Worker / HTTP Cache API) if present
    if ('caches' in window && window.caches) {
      window.caches
        .keys()
        .then((names) => {
          for (const name of names) {
            window.caches.delete(name);
          }
        })
        .catch(() => {});
    }
  } catch {
    // Ignore Cache API errors
  }

  try {
    // 4. Blur active input element to flush browser form autofill state
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  } catch {
    // Ignore DOM errors
  }
}

/**
 * Clears all client-side form caches and redirects the application back to the Home view ('/').
 */
export function redirectToHomeAfterSubmission(onComplete?: (() => void) | number): void {
  clearClientSubmissionCache();

  const triggerRedirect = () => {
    if (typeof window !== 'undefined') {
      try {
        window.history.pushState(null, '', '/');
      } catch {
        window.location.hash = '/';
      }
      window.dispatchEvent(new CustomEvent('xweba:redirect-home'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (typeof onComplete === 'function') {
      onComplete();
    }
  };

  if (typeof onComplete === 'number' && onComplete > 0) {
    setTimeout(triggerRedirect, onComplete);
  } else {
    triggerRedirect();
  }
}

export function encodePayloadBackup(jsonStr: string): string {
  try {
    if (!jsonStr || jsonStr.length > 6000) return '';
    const bytes = new TextEncoder().encode(jsonStr);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  } catch {
    return '';
  }
}

export async function safeFetchJson<T = any>(
  input: RequestInfo | URL,
  init?: SafeFetchOptions
): Promise<ApiResponse<T>> {
  const maxRetries = typeof init?.retries === 'number' ? init.retries : 3;
  const retryDelayMs = init?.retryDelayMs || 700;

  const rawUrlStr = typeof input === 'string' ? input : input.toString();
  const isMailOrAuditEndpoint =
    rawUrlStr.includes('/api/contact') ||
    rawUrlStr.includes('/api/submit-quote') ||
    rawUrlStr.includes('/api/submit-audit') ||
    rawUrlStr.includes('/api/audit') ||
    rawUrlStr.includes('mail-dispatch.php');

  const isApiEndpoint = rawUrlStr.includes('/api/');

  const bodyStr = typeof init?.body === 'string' ? init.body : '';
  let parsedBodyObj: Record<string, any> | null = null;
  if (bodyStr) {
    try {
      parsedBodyObj = JSON.parse(bodyStr);
    } catch {
      parsedBodyObj = null;
    }
  }

  const encodedBackup = isApiEndpoint && bodyStr ? encodePayloadBackup(bodyStr) : '';

  // Always append cache-busting timestamp _t=... on all /api/ requests so browser/proxy never serves stale CRM data
  let effectiveUrl = rawUrlStr;
  if (isApiEndpoint) {
    const sep = effectiveUrl.includes('?') ? '&' : '?';
    const params: string[] = [`_t=${Date.now()}`];
    if (isMailOrAuditEndpoint && encodedBackup && !rawUrlStr.includes('_p=')) {
      params.push(`_p=${encodeURIComponent(encodedBackup)}`);
    }
    effectiveUrl = `${effectiveUrl}${sep}${params.join('&')}`;
  }

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const res = await fetch(effectiveUrl, {
        ...init,
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          ...(encodedBackup ? { 'X-Xweba-Payload': encodedBackup } : {}),
          ...(init?.headers || {}),
        },
      });

      const contentType = (res.headers.get('content-type') || '').toLowerCase();

      // 1. If standard JSON response
      if (contentType.includes('application/json')) {
        try {
          const data = await res.json();
          if (isMailOrAuditEndpoint) {
            recordLocalSubmissionReading(rawUrlStr, parsedBodyObj, data);
            clearClientSubmissionCache();
          }
          return {
            ok: res.ok,
            status: res.status,
            data,
            error: !res.ok ? (data?.error || data?.message || `HTTP ${res.status} Error`) : undefined,
          };
        } catch {
          return {
            ok: false,
            status: res.status,
            data: null as unknown as T,
            error: 'Invalid JSON payload received from server.',
          };
        }
      }

      // 2. If non-JSON response (HTML from warmup proxy, 404 on static/PHP host, 502, 503, etc.)
      const text = await res.text();

      // Try parsing as JSON in case PHP returned JSON with text/html content-type
      try {
        const parsedDirect = JSON.parse(text);
        if (parsedDirect && typeof parsedDirect === 'object') {
          if (isMailOrAuditEndpoint) {
            recordLocalSubmissionReading(rawUrlStr, parsedBodyObj, parsedDirect);
            clearClientSubmissionCache();
          }
          return {
            ok: res.ok,
            status: res.status,
            data: parsedDirect as T,
            error: !res.ok ? (parsedDirect?.error || parsedDirect?.message) : undefined,
          };
        }
      } catch {
        // Not JSON, continue to fallback
      }

      // Automatic fallback to native Hostinger PHP dispatcher (/api/mail-dispatch.php) for any /api/ endpoint
      if (
        (res.status === 404 || text.includes('<!DOCTYPE') || text.includes('<html')) &&
        isApiEndpoint &&
        !rawUrlStr.includes('mail-dispatch.php')
      ) {
        try {
          const endpointHint = rawUrlStr.replace(/^.*\/api\//, '').split('?')[0];
          const phpQuery = `?endpoint=${encodeURIComponent(endpointHint)}&_t=${Date.now()}${
            encodedBackup ? `&_p=${encodeURIComponent(encodedBackup)}` : ''
          }`;
          const phpRes = await fetch(`/api/mail-dispatch.php${phpQuery}`, {
            ...init,
            cache: 'no-store',
            headers: {
              Accept: 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              Pragma: 'no-cache',
              ...(encodedBackup ? { 'X-Xweba-Payload': encodedBackup } : {}),
              ...(init?.headers || {}),
            },
          });
          const phpText = await phpRes.text();
          const phpData = JSON.parse(phpText);
          if (phpData && typeof phpData === 'object') {
            if (isMailOrAuditEndpoint) {
              recordLocalSubmissionReading(rawUrlStr, parsedBodyObj, phpData);
              clearClientSubmissionCache();
            }
            return {
              ok: phpRes.ok,
              status: phpRes.status,
              data: phpData as T,
              error: !phpRes.ok ? (phpData?.error || phpData?.message) : undefined,
            };
          }
        } catch {
          // Continue to standard retry logic if PHP fallback not available
        }
      }

      const isWarmupOrHtml =
        text.includes('<!DOCTYPE') ||
        text.includes('<html') ||
        text.includes('Starting Server') ||
        res.status === 404 ||
        res.status === 502 ||
        res.status === 503 ||
        res.status === 504;

      if (isWarmupOrHtml && attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, retryDelayMs * (attempt + 1)));
        continue;
      }

      if (isMailOrAuditEndpoint && parsedBodyObj) {
        recordLocalSubmissionReading(rawUrlStr, parsedBodyObj);
      }

      let userFriendlyError = `Request failed (status ${res.status}).`;

      if (text.includes('<!DOCTYPE') || text.includes('<html') || text.includes('Starting Server')) {
        if (res.status === 404) {
          userFriendlyError = 'API endpoint is temporarily initializing. Please click Retry in a moment.';
        } else if (res.status === 502 || res.status === 503 || res.status === 504) {
          userFriendlyError = 'Server gateway is warming up. Please click Retry in a few seconds.';
        } else {
          userFriendlyError = 'Server is currently initializing. Please try again.';
        }
      } else if (text.trim().length > 0 && text.trim().length < 250) {
        userFriendlyError = text.trim();
      }

      return {
        ok: false,
        status: res.status,
        data: { success: false, error: userFriendlyError } as unknown as T,
        error: userFriendlyError,
      };
    } catch (networkErr: any) {
      if (attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, retryDelayMs * (attempt + 1)));
        continue;
      }

      if (isMailOrAuditEndpoint && parsedBodyObj) {
        recordLocalSubmissionReading(rawUrlStr, parsedBodyObj);
      }

      const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
      const msg = isOffline
        ? 'No internet connection detected. Please check your network.'
        : (networkErr?.message || 'Network connection failed. Please try again.');

      return {
        ok: false,
        status: 0,
        data: { success: false, error: msg } as unknown as T,
        error: msg,
      };
    }
  }

  return {
    ok: false,
    status: 0,
    data: { success: false, error: 'Connection timed out during initialization.' } as unknown as T,
    error: 'Connection timed out during initialization.',
  };
}
